import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from '@roger/ui-components';
import {
  Loader2,
  MoreVertical,
  Plus,
  RefreshCw,
  Save,
  Settings2,
  Trash2,
  Users,
} from 'lucide-react';
import {
  getOnboardingTeamConfig,
  syncOnboardingTeam,
  updateOnboardingTeamConfig,
} from '../../services/api';
import type { OnboardingTeamConfigResponse } from '../../services/api';
import { useStore } from '../../store';
import { EMAIL_REGEX } from '../../utils/emailUtils';
import { useShallow } from 'zustand/react/shallow';
import {
  DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS,
  type OnboardingTeamMemberDraft,
} from './onboardingTeamMember';
import { OnboardingTeamMemberRolesDialog } from './OnboardingTeamMemberRolesDialog';

const defaultRoleSet = new Set<string>(DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS);

const rolesMatchDefaults = (roleSlugs: string[]): boolean => {
  if (roleSlugs.length !== DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS.length) {
    return false;
  }
  return roleSlugs.every(slug => defaultRoleSet.has(slug));
};

const formatMemberRolesSummary = (roleSlugs: string[]): string => {
  if (roleSlugs.length === 0) {
    return 'No roles configured';
  }
  if (rolesMatchDefaults(roleSlugs)) {
    return 'Default roles';
  }

  const roleSet = new Set(roleSlugs);
  const missing = DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS.filter(
    slug => !roleSet.has(slug)
  );
  const extra = roleSlugs.filter(slug => !defaultRoleSet.has(slug));

  if (missing.length > 0) {
    return roleSlugs.join(', ');
  }

  return `Default roles${extra.length > 0 ? `, ${extra.join(', ')}` : ''}`;
};

const membersEqual = (
  a: OnboardingTeamMemberDraft[],
  b: OnboardingTeamMemberDraft[]
): boolean => {
  if (a.length !== b.length) return false;
  return a.every((member, index) => {
    const other = b[index];
    if (!other || member.email !== other.email) return false;
    if (member.role_slugs.length !== other.role_slugs.length) return false;
    return member.role_slugs.every(
      (slug, slugIndex) => slug === other.role_slugs[slugIndex]
    );
  });
};

export const OnboardingTeamPanel: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [members, setMembers] = useState<OnboardingTeamMemberDraft[]>([]);
  const [savedMembers, setSavedMembers] = useState<OnboardingTeamMemberDraft[]>(
    []
  );
  const [newEmail, setNewEmail] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [rolesDialogMember, setRolesDialogMember] =
    useState<OnboardingTeamMemberDraft | null>(null);

  const hasChanges = !membersEqual(members, savedMembers);
  const hasMembers = members.length > 0;
  const showSave = hasMembers && hasChanges;
  const showSync = hasMembers && !hasChanges;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOnboardingTeamConfig({ throwOnError: true });
      const config = res.data as OnboardingTeamConfigResponse;
      const loaded = (config.members ?? []).map(member => ({
        email: member.email,
        role_slugs: [...member.role_slugs],
      }));
      setMembers(loaded);
      setSavedMembers(loaded);
    } catch {
      showError('Failed to load onboarding team config');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const addEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;

    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailInputError('Please enter a valid email address');
      return;
    }
    if (members.some(member => member.email === trimmed)) {
      setEmailInputError('This email has already been added');
      return;
    }

    setMembers(prev => [
      ...prev,
      {
        email: trimmed,
        role_slugs: [...DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS],
      },
    ]);
    setNewEmail('');
    setEmailInputError('');
  };

  const removeMember = (email: string) => {
    setMembers(prev => prev.filter(member => member.email !== email));
  };

  const updateMemberRoles = (updated: OnboardingTeamMemberDraft) => {
    setMembers(prev =>
      prev.map(member =>
        member.email === updated.email
          ? { ...member, role_slugs: [...updated.role_slugs] }
          : member
      )
    );
  };

  const save = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    let membersToSave = members;
    if (trimmed) {
      if (!EMAIL_REGEX.test(trimmed)) {
        setEmailInputError(
          'Please enter a valid email address or click Add to add it'
        );
        return;
      }
      if (!members.some(member => member.email === trimmed)) {
        membersToSave = [
          ...members,
          {
            email: trimmed,
            role_slugs: [...DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS],
          },
        ];
        setMembers(membersToSave);
        setNewEmail('');
        setEmailInputError('');
      }
    }

    setSaving(true);
    try {
      await updateOnboardingTeamConfig({
        throwOnError: true,
        body: { members: membersToSave },
      });
      setSavedMembers(membersToSave);
      showSuccess('Onboarding team saved');
    } catch {
      showError('Failed to save onboarding team');
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await syncOnboardingTeam({ throwOnError: true });
      const result = res.data;
      if (result) {
        showSuccess(
          `Done — ${result.agencies_processed} agencies, ${result.added_count} added` +
            (result.errors.length ? ` (${result.errors.length} errors)` : '')
        );
      }
    } catch {
      showError('Failed to add onboarding team to agencies');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Users className='h-5 w-5' />
            Onboarding Team
          </CardTitle>
        </CardHeader>
        <CardContent className='flex items-center gap-2 text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading…
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className='flex flex-row items-start justify-between gap-4'>
          <div className='space-y-1'>
            <CardTitle className='flex items-center gap-2'>
              <Users className='h-5 w-5' />
              Onboarding Team
            </CardTitle>
            <CardDescription className='text-xs'>
              Auto-add and sync only grant agency access and roles—they never
              remove users or take roles away.
            </CardDescription>
          </div>
          {(showSave || showSync) && (
            <div className='flex gap-2'>
              {showSync && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={sync}
                  disabled={syncing || saving}
                >
                  {syncing ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='h-4 w-4' />
                  )}
                  Add to all existing agencies
                </Button>
              )}
              {showSave && (
                <Button size='sm' onClick={save} disabled={saving || syncing}>
                  {saving ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <Save className='h-4 w-4' />
                  )}
                  Save
                </Button>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className='space-y-3'>
          <div className='flex items-center gap-2'>
            <Input
              placeholder='email@rogerhealthcare.com'
              value={newEmail}
              onChange={e => {
                setNewEmail(e.target.value);
                if (emailInputError) setEmailInputError('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEmail();
                }
              }}
              inputSize='sm'
              className='max-w-xs'
            />
            <Button
              variant='outline'
              size='sm'
              className='h-7'
              onClick={addEmail}
            >
              <Plus className='h-4 w-4' />
              Add
            </Button>
          </div>
          {emailInputError && (
            <p className='text-sm text-destructive'>{emailInputError}</p>
          )}

          {members.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No members configured.
            </p>
          ) : (
            <ul className='grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3'>
              {members.map(member => (
                <li
                  key={member.email}
                  className='flex min-w-0 items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm'
                >
                  <div className='min-w-0 flex-1'>
                    <p className='truncate font-medium'>{member.email}</p>
                    <p className='mt-1 text-xs text-muted-foreground line-clamp-2'>
                      {formatMemberRolesSummary(member.role_slugs)}
                    </p>
                  </div>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-8 w-8 shrink-0 p-0'
                        aria-label={`Actions for ${member.email}`}
                      >
                        <MoreVertical className='h-4 w-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem
                        onClick={() => setRolesDialogMember(member)}
                      >
                        <Settings2 className='mr-2 h-4 w-4' />
                        Edit auto-assign roles
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className='text-destructive focus:text-destructive'
                        onClick={() => removeMember(member.email)}
                      >
                        <Trash2 className='mr-2 h-4 w-4' />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <OnboardingTeamMemberRolesDialog
        open={rolesDialogMember !== null}
        member={rolesDialogMember}
        onClose={() => setRolesDialogMember(null)}
        onSave={updateMemberRoles}
      />
    </>
  );
};

export default OnboardingTeamPanel;
